import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'wouter';
import '../styles/site-nav.css';
import { SIGNUP_URL } from '../constants';
import solutionsData from '../data/solutions.json';
import { RegionSelector } from './RegionSelector';
import type { Region } from '../hooks/useRegion';

/* The nav across the whole site: Giraffe home (Giraffe Developer), the
 * products, the use cases and the resources, a link to the page's own pricing
 * on the product pages that have it, and the two asks (the ecosystem page is
 * left unlinked for now). Fixed at the top,
 * var(--header-height) tall, so every page's own padding for the old header
 * still fits it. Light over the paper pages, dark
 * over the dark drafting ones (`dark`). Menus open on hover or click; on a
 * phone it folds into one panel. Every menu is in the page, hidden until
 * opened, so each page carries its links for search engines. */

type Item = { href: string; label: string; desc?: string; external?: boolean };

const products: Item[] = [
  { href: '/', label: 'Giraffe Developer', desc: 'Build your real estate development system' },
  { href: '/giraffe-design', label: 'Giraffe Design', desc: 'Design, test and present schemes' },
  { href: '/giraffe-city', label: 'Giraffe City', desc: 'Plan and engage for government' },
  { href: '/giraffe-code', label: 'Giraffe Code', desc: 'Build and ship real estate and design apps' },
];

// the use cases for the visitor's region, as the old header listed them (some share a name across regions)
const allUseCases = (solutionsData as { slug: string; name: string; tagline: string; region: string[]; type?: string; publish?: boolean }[])
  .filter((s) => (s.type === 'use-case' || s.type === 'useCase') && s.publish !== false);
const useCasesFor = (region: string): Item[] =>
  allUseCases.filter((s) => s.region.includes(region)).map((s) => ({ href: `/use-cases/${s.slug}`, label: s.name, desc: s.tagline }));

const resources: Item[] = [
  { href: '/plugins', label: 'Plugins and data packs' },
  { href: '/case-studies', label: 'Case studies' },
  { href: '/blog', label: 'Blog' },
  { href: 'https://intercom.help/giraffe-knowledgebase-v6/en/', label: 'Knowledge base', external: true },
  { href: '/enterprise', label: 'Enterprise' },
  { href: '/team', label: 'Team' },
];


// the product pages with a pricing section of their own; the nav links to it there
const PRICED = ['/giraffe-design', '/giraffe-city', '/giraffe-code'];

const link = (it: Item) => (it.external ? { href: it.href, target: '_blank', rel: 'noopener noreferrer' } : { href: it.href });

export function SiteNav({ dark = false, tone, region = 'au', onRegionChange }: { dark?: boolean; tone?: 'green' | 'sage' | 'brown'; region?: Region; onRegionChange?: (r: Region) => void }) {
  const menus: { key: string; label: string; items: Item[]; wide?: boolean }[] = [
    { key: 'products', label: 'Products', items: products },
    { key: 'use-cases', label: 'Use cases', items: useCasesFor(region), wide: true },
    { key: 'resources', label: 'Resources', items: resources },
  ];
  const [open, setOpen] = useState<string | null>(null);
  const [mobile, setMobile] = useState(false);
  const [location] = useLocation();
  const hasPricing = PRICED.includes(location.replace(/\/$/, ''));
  const ref = useRef<HTMLElement>(null);
  const closing = useRef<number | null>(null);

  // a new page closes everything
  useEffect(() => { setOpen(null); setMobile(false); }, [location]);

  // a click elsewhere, or Escape, closes the menus
  useEffect(() => {
    const away = (e: Event) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(null); setMobile(false); } };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(null); setMobile(false); } };
    document.addEventListener('click', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('click', away); document.removeEventListener('keydown', esc); };
  }, []);

  const hold = (key: string) => { if (closing.current) clearTimeout(closing.current); setOpen(key); };
  const release = () => { closing.current = window.setTimeout(() => setOpen(null), 160); };

  return (
    <header class={['sn', dark && 'sn-dark', tone && `sn-${tone}`].filter(Boolean).join(' ')} ref={ref}>
      <div class="sn-in">
        <a href="/" class="sn-brand" aria-label="Giraffe home">
          {/* on the dark pages the mark is its white outline */}
          <img src={dark ? '/giraffe-outline-white.svg' : '/giraffe.svg'} alt="" />
          <span>Giraffe</span>
        </a>

        <nav class="sn-links" aria-label="Main">
          {menus.map((m) => (
            <div class="sn-menu" key={m.key} onMouseEnter={() => hold(m.key)} onMouseLeave={release}>
              <button
                type="button"
                class="sn-link"
                aria-expanded={open === m.key}
                onClick={() => setOpen(open === m.key ? null : m.key)}
              >
                {m.label}<span class="sn-caret" aria-hidden="true">▾</span>
              </button>
              {/* always in the page, hidden until open, so every page carries these links */}
              <div class={m.wide ? 'sn-panel sn-panel-wide' : 'sn-panel'} hidden={open !== m.key}>
                {m.items.map((it) => (
                  <a key={it.href} class="sn-item" {...link(it)}>
                    <b>{it.label}</b>
                    {it.desc && <small>{it.desc}</small>}
                  </a>
                ))}
              </div>
            </div>
          ))}
          {hasPricing && <a class="sn-link" href="#pricing">Pricing</a>}
        </nav>

        <div class="sn-asks">
          {onRegionChange && <RegionSelector region={region} onChange={onRegionChange} />}
          <a class="sn-talk" href="/contact">Talk to us</a>
          <a class="sn-btn" href={SIGNUP_URL}>Start building</a>
        </div>

        <button type="button" class="sn-burger" aria-label="Menu" aria-expanded={mobile} onClick={() => setMobile(!mobile)}>
          <span /><span /><span />
        </button>
      </div>

      {/* the phone menu, likewise always in the page and hidden until opened */}
      <div class="sn-mobile" hidden={!mobile}>
        {menus.map((m) => (
          <div class="sn-mobile-group" key={m.key}>
            <p class="sn-mobile-head">{m.label}</p>
            {m.items.map((it) => <a key={it.href} class="sn-mobile-item" {...link(it)}>{it.label}</a>)}
          </div>
        ))}
        <div class="sn-mobile-group">
          {hasPricing && <a class="sn-mobile-item" href="#pricing">Pricing</a>}
          <a class="sn-mobile-item" href="/contact">Talk to us</a>
        </div>
        {onRegionChange && <div class="sn-mobile-region"><RegionSelector region={region} onChange={onRegionChange} /></div>}
        <a class="sn-btn sn-btn-block" href={SIGNUP_URL}>Start building</a>
      </div>
    </header>
  );
}
